import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SelectionGroup } from '../../components/common/SelectionGroup/SelectionGroup';
import { ButtonLarge } from '../../components/common/buttons/ButtonLarge/ButtonLarge';
import { QuizFooter, QuizFooterRow } from '../../components/common/QuizFooter';
import { useGame } from '../../context/GameContext';
import {
  getActionConfig,
  getAutoConfig,
  buildBlastConfig,
  buildFixedHandsConfig,
  createRandomBlastSelection,
  createRandomFixedHandsConfig,
  STACKS_BY_FORMAT,
} from './blastRangeRules';
import { BlastPageHeader } from '../../components/blast/BlastPageHeader/BlastPageHeader';

import './blast-config.css';

// ─── Constants ───────────────────────────────────────────────────────────────

const FORMATS = ['3 Way', 'Heads Up'];

const POSITIONS_BY_FORMAT = {
  '3 Way':    ['BTN', 'SB', 'BB'],
  'Heads Up': ['SB', 'BB'],
};

// Display-name → internal format key
const FORMAT_KEY = { '3 Way': '3way', 'Heads Up': 'hu' };
// Internal format key → display-name (reverse of FORMAT_KEY)
const FORMAT_KEY_TO_LABEL = { '3way': '3 Way', 'hu': 'Heads Up' };
// Internal study-mode token → display-name (matches the Study Mode options)
const STUDY_MODE_TO_LABEL = { fixedBlinds: 'Fixed Blinds', fixedHands: 'Mixed Blinds' };

// Big-blind options for the Fixed Blinds selector (display key → stacks array)
const STACKS_BY_FORMAT_POSITION = {
  '3 Way': {
    BTN: [15, 12, 9, 6, 4],
    SB:  [15, 12, 9, 7, 6, 3],
    BB:  [15, 12, 10, 9, 7, 5, 4],
  },
  'Heads Up': {
    SB: [25, 15, 12, 10, 8, 6, 3, 2],
    BB: [25, 15, 10, 8, 6, 4, 3],
  },
};

// ─── Component ───────────────────────────────────────────────────────────────

export const BlastConfigScreen = () => {
  const navigate      = useNavigate();
  const location       = useLocation();
  const { startNewGame } = useGame();

  // ── Restore from the range screen's "Back" button ─────────────────────────
  // When Continue/random-fill sends a Fixed Blinds config to the range screen,
  // that screen's Back button navigates here with the full selection so we
  // can repopulate exactly what was chosen (manually adjust, or Reconfigure
  // again) instead of starting over from a blank screen.
  const restore = location.state?.restoreConfig ?? null;

  // ── Study mode ────────────────────────────────────────────────────────────
  const [selectedStudyMode, setSelectedStudyMode] = useState(
    restore ? [STUDY_MODE_TO_LABEL[restore.studyMode] ?? restore.studyMode] : []
  );
  // Derive internal key from selection
  const studyMode = selectedStudyMode[0] === 'Fixed Blinds' ? 'fixedBlinds'
                  : selectedStudyMode[0] === 'Mixed Blinds'  ? 'fixedHands'
                  : null;

  // ── Selection state ───────────────────────────────────────────────────────
  const [selectedFormat,   setSelectedFormat]   = useState(
    restore ? [FORMAT_KEY_TO_LABEL[restore.format] ?? restore.format] : []
  );
  const [selectedPosition, setSelectedPosition] = useState(restore ? [restore.position] : []);
  const [selectedStack,    setSelectedStack]     = useState(
    restore && restore.stack != null ? [String(restore.stack)] : []
  );
  const [selectedPrimary,  setSelectedPrimary]   = useState(restore ? [restore.primaryAction] : []);
  const [selectedSecondary, setSelectedSecondary] = useState(restore?.secondaryAction ? [restore.secondaryAction] : []);
  const [selectedTertiary,  setSelectedTertiary]  = useState(restore?.tertiaryAction ? [restore.tertiaryAction] : []);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [error,          setError]          = useState(null);
  // Tracks whether the current selections were populated by "Configure a Random Quiz".
  // Only true after handleRandomConfig runs; cleared on any manual selection via resetFrom.
  const [isRandomConfig, setIsRandomConfig] = useState(!!restore?.isRandomConfig);
  // Tracks whether the STUDY MODE itself was picked by the random coin flip
  // (as opposed to the user manually clicking Fixed Blinds / Mixed Blinds).
  // Only true when Configure a Random Quiz ran with no study mode chosen yet;
  // cleared the moment the user manually picks a study mode. Lets
  // Reconfigure Quiz give the study mode another chance to flip on each
  // press when it was random to begin with, instead of getting stuck on
  // whichever mode the very first press happened to land on.
  const [studyModeWasRandom, setStudyModeWasRandom] = useState(!!restore?.studyModeWasRandom);

  // ── Reset helpers ─────────────────────────────────────────────────────────
  const resetFrom = (level) => {
    if (level <= 0) { setSelectedFormat([]); }
    if (level <= 1) { setSelectedPosition([]); }
    if (level <= 2) { setSelectedStack([]); }
    if (level <= 3) { setSelectedPrimary([]); }
    if (level <= 4) { setSelectedSecondary([]); }
    if (level <= 5) { setSelectedTertiary([]); }
    setError(null);
    setIsRandomConfig(false);
  };

  const handleReset = () => {
    setSelectedStudyMode([]);
    resetFrom(0);
    setIsRandomConfig(false);
    setStudyModeWasRandom(false);
  };

  // Switching study mode resets all downstream selections
  const handleStudyModeSelect = (opt) => {
    if (selectedStudyMode[0] === opt) return;
    setSelectedStudyMode([opt]);
    setStudyModeWasRandom(false); // manually chosen — no longer up for grabs on Reconfigure
    resetFrom(0); // clear format and everything below
  };

  // ── Change handlers ───────────────────────────────────────────────────────
  const handleFormatChange   = (opt) => { setSelectedFormat([opt]);   resetFrom(1); };
  const handlePositionChange = (opt) => { setSelectedPosition([opt]); resetFrom(2); };
  const handleStackChange    = (opt) => { setSelectedStack([opt]);    resetFrom(3); };

  const handlePrimaryChange = (key) => {
    setSelectedPrimary([key]);
    resetFrom(4);
  };

  const handleSecondaryChange = (key) => {
    setSelectedSecondary([key]);
    setSelectedTertiary([]);
    setError(null);
  };

  const handleTertiaryChange = (key) => {
    setSelectedTertiary([key]);
    setError(null);
  };

  // ── Derived rule ──────────────────────────────────────────────────────────
  const format   = selectedFormat[0] === '3 Way' ? '3way' : selectedFormat[0] === 'Heads Up' ? 'hu' : null;
  const position = selectedPosition[0] ?? null;
  const stack    = selectedStack[0] ? parseInt(selectedStack[0]) : null;

  // For Fixed Hands, derive rule from the largest available stack (reference stack).
  // This gives the most complete set of primary/secondary options to display.
  const fixedHandsRefStack = (studyMode === 'fixedHands' && format && position)
    ? (() => {
        const stacks = STACKS_BY_FORMAT[format]?.[position] ?? [];
        let bestStack = stacks[0] ?? null;
        let bestCount = 0;
        for (const s of stacks) {
          const r = getActionConfig(format, position, s);
          const count = r?.primaryActions?.length ?? 0;
          if (count > bestCount) { bestCount = count; bestStack = s; }
        }
        return bestStack;
      })()
    : null;

  const effectiveStack = studyMode === 'fixedHands' ? fixedHandsRefStack : stack;

  const rule      = format && position && effectiveStack ? getActionConfig(format, position, effectiveStack) : null;
  const autoConf  = rule ? getAutoConfig(rule) : null;

  // Auto-select primary when there is only one opening action.
  // For Fixed Hands: trigger when position is chosen; for Fixed Blinds: when stack is chosen.
  const autoTrigger = studyMode === 'fixedHands' ? selectedPosition[0] : selectedStack[0];
  useEffect(() => {
    if (autoConf && autoTrigger && selectedPrimary.length === 0) {
      setSelectedPrimary([autoConf.primaryAction]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoTrigger]);

  // ── Primary action rows ───────────────────────────────────────────────────
  const { row1Actions, row2Actions, isAutoSelected } = (() => {
    if (!rule) return { row1Actions: [], row2Actions: [], isAutoSelected: false };
    const { primaryActions, primaryRowBreak } = rule;
    const r1 = primaryRowBreak != null ? primaryActions.slice(0, primaryRowBreak) : primaryActions;
    const r2 = primaryRowBreak != null ? primaryActions.slice(primaryRowBreak)    : [];
    const auto = autoConf != null && r1.length === 1;
    return { row1Actions: r1, row2Actions: r2, isAutoSelected: auto };
  })();

  const primaryAction   = selectedPrimary[0]   ?? null;
  const secondaryAction = selectedSecondary[0] ?? null;
  const tertiaryAction  = selectedTertiary[0]  ?? null;

  // ── Secondary config ──────────────────────────────────────────────────────
  const secondaryCfg = primaryAction && rule ? (rule.secondary?.[primaryAction] ?? null) : null;

  // The secondary label / options depend on whether the selected secondary is
  // a CSV-changer that provides its own tertiary, or a plain filter.
  const selectedSecOpt = secondaryAction && secondaryCfg
    ? secondaryCfg.options.find(o => o.key === secondaryAction) ?? null
    : null;

  const tertiaryCfg = selectedSecOpt?.tertiary ?? null;

  // ── Complete check ────────────────────────────────────────────────────────
  // For Fixed Blinds: needs a stack + primary action.
  // For Fixed Hands: needs a position + primary action (no stack required).
  // Secondary and tertiary are always optional unless the section is marked non-optional.
  const isConfigComplete = () => {
    if (!studyMode) return false;
    // If a non-optional secondary section (e.g. "Your Response") is visible, it must have a selection.
    if (primaryAction && secondaryCfg && !secondaryCfg.isOptional && !secondaryAction) return false;
    // If a non-optional tertiary section is visible, it must have a selection too.
    if (secondaryAction && tertiaryCfg && !tertiaryCfg.isOptional && !tertiaryAction) return false;
    if (studyMode === 'fixedHands') {
      if (selectedPosition.length === 0 || selectedPrimary.length === 0) return false;
      // Ensure there are valid stacks for this primary action before allowing submission.
      // e.g. BTN open_raise has no valid stacks in 3-way (BTN never min-raises).
      if (format && position && primaryAction) {
        const testCfg = buildFixedHandsConfig(format, position, primaryAction, secondaryAction || null, tertiaryAction || null);
        if (testCfg.availableStacks.length === 0) return false;
      }
      return true;
    }
    return selectedStack.length > 0 && selectedPrimary.length > 0;
  };

  // ── Random config ─────────────────────────────────────────────────────────
  // The random button stays visible for the entire config journey — it only
  // disappears once the config is complete enough to submit (at which point
  // it's replaced by Begin Quiz / Continue).
  const showRandomButton = !isConfigComplete();

  // ── Scroll gradient + auto-reveal ─────────────────────────────────────────
  // Sections render one at a time as the user completes each prior one (see
  // project rule), so the scrollable area's content height keeps growing
  // through the whole config flow — not just once Begin Quiz/Continue
  // appears. The gradient needs to track actual overflow (scrollHeight >
  // clientHeight), not isConfigComplete(), so it shows the moment there's
  // really something to scroll to. Recomputed after every selection change,
  // once the newly-revealed section has actually painted.
  const scrollContainerRef  = useRef(null);
  const prevScrollHeightRef = useRef(null);
  const [canScrollConfig, setCanScrollConfig] = useState(false);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return undefined;

    const raf = requestAnimationFrame(() => {
      const newHeight = el.scrollHeight;
      setCanScrollConfig(newHeight > el.clientHeight + 1);

      // Auto-reveal: when a selection just added a new section below the
      // fold, scroll it into view instead of leaving it hidden with only
      // the gradient as a hint some users won't notice. Skipped on the
      // very first measurement (mount / restored config) so opening the
      // screen never auto-scrolls on its own — only a genuine, user-caused
      // content increase does.
      if (prevScrollHeightRef.current !== null && newHeight > prevScrollHeightRef.current) {
        el.scrollTo({ top: newHeight, behavior: 'smooth' });
      }
      prevScrollHeightRef.current = newHeight;
    });

    return () => cancelAnimationFrame(raf);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudyMode, selectedFormat, selectedPosition, selectedStack,
      selectedPrimary, selectedSecondary, selectedTertiary]);

  // Viewport-size changes (rotation, on-screen keyboard, etc.) can flip
  // whether the same content overflows, independent of any selection change.
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return undefined;
    const recompute = () => setCanScrollConfig(el.scrollHeight > el.clientHeight + 1);
    window.addEventListener('resize', recompute);
    return () => window.removeEventListener('resize', recompute);
  }, []);

  const handleRandomConfig = () => {
    // If Study Mode hasn't been chosen yet, randomize between the two, and
    // remember that it was random so Reconfigure Quiz can keep re-flipping it.
    const modeWasUnset = studyMode === null;
    const mode = studyMode ?? (Math.random() < 0.5 ? 'fixedBlinds' : 'fixedHands');
    if (modeWasUnset) setStudyModeWasRandom(true);

    if (mode === 'fixedBlinds') {
      // Fixed Blinds: fill in whatever the user hasn't picked yet and
      // populate the UI so the user can review (Reconfigure / Continue)
      // before moving on to the range screen — same pattern as Mixed Blinds.
      const overrides = {};
      if (selectedFormat[0])   overrides.format   = FORMAT_KEY[selectedFormat[0]];
      if (selectedPosition[0]) overrides.position = selectedPosition[0];
      if (selectedStack[0])    overrides.stack    = parseInt(selectedStack[0]);
      if (selectedPrimary[0])  overrides.primaryAction   = selectedPrimary[0];
      if (selectedSecondary[0]) overrides.secondaryAction = selectedSecondary[0];
      if (selectedTertiary[0])  overrides.tertiaryAction  = selectedTertiary[0];

      const sel = createRandomBlastSelection(overrides);
      if (!sel) return;

      if (modeWasUnset) setSelectedStudyMode(['Fixed Blinds']);
      setSelectedFormat([FORMAT_KEY_TO_LABEL[sel.format] ?? sel.format]);
      setSelectedPosition([sel.position]);
      setSelectedStack([String(sel.stack)]);
      setSelectedPrimary([sel.primaryAction]);
      setSelectedSecondary(sel.secondaryAction ? [sel.secondaryAction] : []);
      setSelectedTertiary(sel.tertiaryAction  ? [sel.tertiaryAction]  : []);
      setIsRandomConfig(true);
      return;
    }

    // Mixed Blinds: fill in whatever's left and populate the UI so the user
    // can review (and Reconfigure / Begin Quiz) before starting.
    const overrides = {};
    if (selectedFormat[0])   overrides.format   = FORMAT_KEY[selectedFormat[0]];
    if (selectedPosition[0]) overrides.position = selectedPosition[0];
    if (selectedPrimary[0])  overrides.primaryAction   = selectedPrimary[0];
    if (selectedSecondary[0]) overrides.secondaryAction = selectedSecondary[0];
    if (selectedTertiary[0])  overrides.tertiaryAction  = selectedTertiary[0];

    const rand = createRandomFixedHandsConfig(overrides);
    if (!rand) return;

    if (modeWasUnset) setSelectedStudyMode(['Mixed Blinds']);
    setSelectedFormat([FORMAT_KEY_TO_LABEL[rand.format] ?? rand.format]);
    setSelectedPosition([rand.position]);
    setSelectedPrimary([rand.primaryAction]);
    setSelectedSecondary(rand.secondaryAction ? [rand.secondaryAction] : []);
    setSelectedTertiary(rand.tertiaryAction  ? [rand.tertiaryAction]  : []);
    setIsRandomConfig(true);
  };

  // "Reconfigure Quiz" only ever appears once every field is already filled
  // in (isConfigComplete === true), so handleRandomConfig's "fill in
  // whatever's blank" logic would have nothing left to fill and just
  // re-set the same values. This draws a completely fresh random quiz
  // instead, ignoring the current selections.
  //
  // If the study mode itself was originally picked by the random coin flip
  // (studyModeWasRandom), give it another chance to flip on every press too —
  // otherwise repeated re-rolls get stuck on whichever mode the very first
  // press happened to land on. If the user manually picked the study mode,
  // it stays locked and only the rest gets re-rolled.
  const handleReconfigureQuiz = () => {
    const mode = studyModeWasRandom
      ? (Math.random() < 0.5 ? 'fixedBlinds' : 'fixedHands')
      : studyMode;

    if (mode === 'fixedBlinds') {
      const sel = createRandomBlastSelection();
      if (!sel) return;

      if (mode !== studyMode) setSelectedStudyMode(['Fixed Blinds']);
      setSelectedFormat([FORMAT_KEY_TO_LABEL[sel.format] ?? sel.format]);
      setSelectedPosition([sel.position]);
      setSelectedStack([String(sel.stack)]);
      setSelectedPrimary([sel.primaryAction]);
      setSelectedSecondary(sel.secondaryAction ? [sel.secondaryAction] : []);
      setSelectedTertiary(sel.tertiaryAction  ? [sel.tertiaryAction]  : []);
      setIsRandomConfig(true);
      return;
    }

    const rand = createRandomFixedHandsConfig();
    if (!rand) return;

    if (mode !== studyMode) setSelectedStudyMode(['Mixed Blinds']);
    setSelectedFormat([FORMAT_KEY_TO_LABEL[rand.format] ?? rand.format]);
    setSelectedPosition([rand.position]);
    setSelectedPrimary([rand.primaryAction]);
    setSelectedSecondary(rand.secondaryAction ? [rand.secondaryAction] : []);
    setSelectedTertiary(rand.tertiaryAction  ? [rand.tertiaryAction]  : []);
    setIsRandomConfig(true);
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleNext = () => {
    if (!format || !position || !primaryAction) return;

    if (studyMode === 'fixedHands') {
      const config = buildFixedHandsConfig(format, position, primaryAction, secondaryAction, tertiaryAction);
      startNewGame(config);
      setTimeout(() => navigate('/blast'), 100);
      return;
    }

    // Fixed Blinds — original flow via range screen. Carry isRandomConfig and
    // studyModeWasRandom along so the range screen's Back button can restore
    // both correctly.
    if (!stack) return;
    const config = buildBlastConfig(format, position, stack, primaryAction, secondaryAction, tertiaryAction);
    navigate('/blast-range', { state: { config, isRandom: isRandomConfig, studyModeWasRandom } });
  };

  // ── Helpers for SelectionGroup ────────────────────────────────────────────
  const primaryOptions  = row1Actions.map(a => a.label);
  // When auto-select is active, show row1 button selected only if current
  // primaryAction belongs to row1 (user may have switched to a row2 option).
  const isCurrentlyOnRow1 = row1Actions.some(a => a.key === primaryAction);
  const primarySelected   = (isAutoSelected && isCurrentlyOnRow1)
    ? primaryOptions.slice(0, 1)
    : row1Actions.find(a => a.key === primaryAction)?.label
      ? [row1Actions.find(a => a.key === primaryAction).label]
      : [];

  const row2Options   = row2Actions.map(a => a.label);
  const row2Selected  = primaryAction
    ? row2Actions.find(a => a.key === primaryAction)?.label
      ? [row2Actions.find(a => a.key === primaryAction).label]
      : []
    : [];

  // Convert label back to key for change handlers
  const labelToKey = (label) => {
    const all = rule?.primaryActions ?? [];
    return all.find(a => a.label === label)?.key ?? label;
  };

  const secOptions  = secondaryCfg ? secondaryCfg.options.map(o => o.label) : [];
  const secSelected = secondaryAction && secondaryCfg
    ? [secondaryCfg.options.find(o => o.key === secondaryAction)?.label].filter(Boolean)
    : [];

  const secLabelToKey = (label) =>
    secondaryCfg?.options.find(o => o.label === label)?.key ?? label;

  const terOptions  = tertiaryCfg ? tertiaryCfg.options.map(o => o.label) : [];
  const terSelected = tertiaryAction && tertiaryCfg
    ? [tertiaryCfg.options.find(o => o.key === tertiaryAction)?.label].filter(Boolean)
    : [];

  const terLabelToKey = (label) =>
    tertiaryCfg?.options.find(o => o.label === label)?.key ?? label;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="blast-screen">
      <div className="blast-screen-content">

        <div className="blast-config-scroll-wrapper">
          <div className="blast-config-scroll" ref={scrollContainerRef}>
            {/* Sticky header */}
            <div className="blast-config-sticky-header">
              <BlastPageHeader
                leftLink={{ label: 'Dashboard', onClick: () => navigate('/') }}
                rightLink={{ label: 'Reset', onClick: handleReset }}
              />
            </div>

            {error && <div className="blast-error">{error}</div>}

            {/* 0. Study Mode */}
            <SelectionGroup
              title="Choose a Study Mode"
              description="Focus on one stack depth to build your fundamentals, or practice across multiple stack depths to improve consistency."
              options={['Fixed Blinds', 'Mixed Blinds']}
              selectedOptions={selectedStudyMode}
              onSelect={handleStudyModeSelect}
              className="config"
            />

            {/* 1. Players — appears after Study Mode is chosen */}
            {studyMode !== null && (
              <SelectionGroup
                title="Choose a Format"
                options={FORMATS}
                selectedOptions={selectedFormat}
                onSelect={handleFormatChange}
                className="config"
              />
            )}

            {/* 2. Your Position */}
            {selectedFormat.length > 0 && (
              <SelectionGroup
                title="What's Your Position?"
                options={POSITIONS_BY_FORMAT[selectedFormat[0]] ?? []}
                selectedOptions={selectedPosition}
                onSelect={handlePositionChange}
                className="config"
              />
            )}

            {/* 3. Big Blinds — Fixed Blinds only */}
            {studyMode === 'fixedBlinds' && selectedPosition.length > 0 && (
              <SelectionGroup
                title="Stack Size (Big Blinds)"
                options={(STACKS_BY_FORMAT_POSITION[selectedFormat[0]]?.[selectedPosition[0]] ?? []).map(String)}
                selectedOptions={selectedStack.map(String)}
                onSelect={handleStackChange}
                className="config"
              />
            )}

            {/* 4. Primary row 1 — opening types
                Fixed Blinds: shows after stack is chosen
                Fixed Hands:  shows after position is chosen (reference stack used) */}
            {(studyMode === 'fixedHands'
                ? selectedPosition.length > 0
                : selectedStack.length > 0)
              && row1Actions.length > 0 && (
              <SelectionGroup
                title={rule.primaryLabel}
                options={primaryOptions}
                selectedOptions={isAutoSelected ? primaryOptions.slice(0, 1) : primarySelected}
                onSelect={(label) => handlePrimaryChange(labelToKey(label))}
                className="config"
              />
            )}

            {/* 4b. Primary row 2 — continuation of same section. */}
            {(studyMode === 'fixedHands'
                ? selectedPosition.length > 0
                : selectedStack.length > 0)
              && row2Actions.length > 0 && (
              <div style={{ marginTop: selectedPosition[0] === 'SB' ? '-16px' : '-26px', marginBottom: '32px' }}>
                <SelectionGroup
                  title={null}
                  options={row2Options}
                  selectedOptions={row2Selected}
                  onSelect={(label) => handlePrimaryChange(labelToKey(label))}
                  className="config"
                />
              </div>
            )}

            {/* 5. Secondary (Option Type / Your Response / BB Response Types / Response Types) */}
            {primaryAction && secondaryCfg && (
              <SelectionGroup
                title={secondaryCfg.label}
                isOptional={secondaryCfg.isOptional}
                description={secondaryCfg.subtitle ?? ''}
                options={secOptions}
                selectedOptions={secSelected}
                onSelect={(label) => handleSecondaryChange(secLabelToKey(label))}
                className="config"
              />
            )}

            {/* 6. Tertiary (Your Response after selecting a BB Response Type) */}
            {secondaryAction && tertiaryCfg && (
              <SelectionGroup
                title={tertiaryCfg.label}
                isOptional={tertiaryCfg.isOptional}
                options={terOptions}
                selectedOptions={terSelected}
                onSelect={(label) => handleTertiaryChange(terLabelToKey(label))}
                className="config"
              />
            )}

            <div className="blast-config-scroll-bottom-spacer" />
          </div>

          {/* Gradient tracks real scroll overflow, independent of config completeness */}
          {canScrollConfig && <div className="blast-config-scroll-gradient" aria-hidden="true" />}
        </div>

        {/* Footer */}
        <QuizFooter horizontalPadding={16} rowGap={11}>
          <QuizFooterRow gap={11}>
            {showRandomButton && (
              <ButtonLarge
                text="Configure a Random Quiz"
                buttonLargeClassName="button-large random-config-button"
                onClick={handleRandomConfig}
              />
            )}
            {!showRandomButton && isConfigComplete() && isRandomConfig && (
              <ButtonLarge
                text="Reconfigure Quiz"
                buttonLargeClassName="button-large go-back-button"
                onClick={handleReconfigureQuiz}
              />
            )}
            {!showRandomButton && isConfigComplete() && (
              <ButtonLarge
                text={studyMode === 'fixedHands' ? 'Begin Quiz' : 'Continue'}
                buttonLargeClassName="button-large config-next-button"
                onClick={handleNext}
              />
            )}
          </QuizFooterRow>
        </QuizFooter>

      </div>
    </div>
  );
};

export default BlastConfigScreen;
